import { SiNextdotjs, SiReact, SiTypescript, SiSupabase } from "react-icons/si";
import { Cloud, BrainCircuit, ShieldCheck, Zap, Server, Lock } from "lucide-react";

const techs = [
  { icon: SiNextdotjs, label: "Next.js" },
  { icon: SiReact, label: "React" },
  { icon: SiTypescript, label: "TypeScript" },
  { icon: SiSupabase, label: "Supabase" },
  { icon: BrainCircuit, label: "AI" },
  { icon: Cloud, label: "Cloud" },
];

const traits = [
  { icon: ShieldCheck, title: "Secure architecture", desc: "End-to-end encryption, role-based access, audit-ready." },
  { icon: Server, title: "Production-grade", desc: "Battle-tested infrastructure, observability, SLAs." },
  { icon: Zap, title: "Fast performance", desc: "Sub-100ms interactions, optimized queries, edge delivery." },
  { icon: Lock, title: "Scalable deployment", desc: "Multi-region, autoscaling, zero-downtime upgrades." },
];

export default function TechStack() {
  return (
    <section
      id="stack"
      data-testid="tech-stack-section"
      className="bg-[#050505] text-white py-24 md:py-36 relative overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-white/40">
              04 — Stack
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 leading-[1.05]">
              Engineered for
              <br />
              <span className="text-white/40">enterprise reality.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-lg text-white/60 font-light leading-relaxed">
              A modern, production-grade stack — chosen so your custom system runs
              fast, scales effortlessly, and stays secure.
            </p>
          </div>
        </div>

        {/* Tech logos */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden mb-16">
          {techs.map((t, i) => {
            const Icon = t.icon;
            return (
              <div
                key={i}
                data-testid={`tech-${t.label.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                className="bg-[#050505] p-8 md:p-12 flex flex-col items-center justify-center gap-3 hover:bg-white/5 transition-colors duration-500 group"
              >
                <Icon className="w-8 h-8 md:w-10 md:h-10 text-white/80 group-hover:text-white transition-colors" />
                <span className="text-xs tracking-[0.2em] uppercase text-white/50 group-hover:text-white/80 transition-colors">
                  {t.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Traits */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
          {traits.map((t, i) => {
            const Icon = t.icon;
            return (
              <div
                key={i}
                className="bg-[#050505] p-8 hover:bg-white/5 transition-colors duration-500"
                data-testid={`trait-${i}`}
              >
                <Icon className="w-5 h-5 text-white/80 mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-xl font-normal tracking-tight mb-2">{t.title}</h3>
                <p className="text-sm text-white/55 font-light leading-relaxed">{t.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
